import { effect, inject, signal, Service } from '@angular/core';
import type { HubConnection } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import {
  ChecklistCompletedAction,
  ChecklistDiscardedAction,
  ChecklistItemCompletionTimeUpdatedAction,
  ChecklistItemContentsClearedAction,
  ChecklistItemCustomInputUpdatedAction,
  ChecklistItemFieldUpdatedAction,
  ChecklistItemFileAttachedAction,
  ChecklistItemFileRemovedAction,
  ChecklistItemHeaderUpdatedAction,
  ChecklistItemRemarksUpdatedAction,
  ChecklistItemSlotSelectionUpdatedAction,
  ChecklistItemStatusChangedAction,
  ChecklistItemUndoKeptAction,
  ChecklistRepeatableItemAddedAction,
  ChecklistRepeatableItemDiscardedAction,
  ChecklistViewer,
  FieldEditClaim,
} from '../../modules/checklist/types/generated/checklist-models';
import { fieldRefOf } from '../../modules/checklist/utils/field-ref.util';
import { HubClientBase } from './hub-client.base';
import { OriginIdService } from './origin-id.service';

export const CHECKLIST_DETAIL_HUB_NAME = 'checklist';

export type ChecklistAction =
  | ChecklistCompletedAction
  | ChecklistDiscardedAction
  | ChecklistItemCompletionTimeUpdatedAction
  | ChecklistItemContentsClearedAction
  | ChecklistItemCustomInputUpdatedAction
  | ChecklistItemFieldUpdatedAction
  | ChecklistItemFileAttachedAction
  | ChecklistItemFileRemovedAction
  | ChecklistItemHeaderUpdatedAction
  | ChecklistItemRemarksUpdatedAction
  | ChecklistItemSlotSelectionUpdatedAction
  | ChecklistItemStatusChangedAction
  | ChecklistItemUndoKeptAction
  | ChecklistRepeatableItemAddedAction
  | ChecklistRepeatableItemDiscardedAction;

@Service()
export class ChecklistDetailHubClient extends HubClientBase {
  private readonly originIdService = inject(OriginIdService);

  private checklistId: string | null = null;

  protected get hubName(): string {
    return `${CHECKLIST_DETAIL_HUB_NAME}/${this.checklistId}`;
  }

  private readonly _actions = new Subject<ChecklistAction>();

  private readonly _viewers = signal<readonly ChecklistViewer[]>([]);

  private readonly _viewedItemId = signal<string | null>(null);

  private readonly _fieldActivity = signal<{
    fieldId: string | null;
    isTyping: boolean;
    draft: string | null;
  }>({ fieldId: null, isTyping: false, draft: null });

  get checklistActions$(): Observable<ChecklistAction> {
    return this._actions.asObservable();
  }

  readonly viewers = this._viewers.asReadonly();

  constructor() {
    super();
    effect(() => {
      const itemId = this._viewedItemId();
      if (this.state() === 'live') {
        void this.invoke('SetViewedItem', itemId);
      }
    });
    effect(() => {
      const activity = this._fieldActivity();
      if (this.state() === 'live') {
        void this.invoke(
          'SetFieldActivity',
          activity.fieldId === null ? null : fieldRefOf(activity.fieldId),
          activity.isTyping,
          activity.draft
        );
      }
    });
  }

  setViewedItem(itemId: string | null): void {
    this._viewedItemId.set(itemId);
  }

  setFieldActivity(
    fieldId: string | null,
    isTyping: boolean,
    draft: string | null = null
  ): void {
    const typing = fieldId !== null && isTyping;
    this._fieldActivity.set({
      fieldId,
      isTyping: typing,
      draft: typing ? draft : null,
    });
  }

  clearFieldActivity(fieldId: string): void {
    if (this._fieldActivity().fieldId !== fieldId) return;
    this.setFieldActivity(null, false);
  }

  claimFieldEdit(
    itemId: string,
    fieldId: string
  ): Promise<FieldEditClaim | null> {
    return this.invokeWithResult<FieldEditClaim>(
      'ClaimFieldEdit',
      itemId,
      fieldRefOf(fieldId)
    );
  }

  releaseFieldEdit(itemId: string, fieldId: string): void {
    void this.invoke('ReleaseFieldEdit', itemId, fieldRefOf(fieldId));
  }

  async connectTo(checklistId: string): Promise<void> {
    if (this.checklistId !== checklistId) {
      await this.disconnect();
      this.checklistId = checklistId;
    }
    await this.connect();
  }

  override connect(): Promise<void> {
    if (this.checklistId === null) {
      return Promise.resolve();
    }
    return super.connect();
  }

  override async disconnect(): Promise<void> {
    this.checklistId = null;
    this._viewers.set([]);
    this._viewedItemId.set(null);
    this._fieldActivity.set({ fieldId: null, isTyping: false, draft: null });
    await super.disconnect();
  }

  protected bindServerMethods(connection: HubConnection): void {
    connection.on('ChecklistActionsCommitted', (actions: ChecklistAction[]) => {
      for (const action of actions) {
        if (action.originId !== this.originIdService.originId) {
          this._actions.next(action);
        }
      }
    });

    connection.on('ChecklistViewersChanged', (viewers: ChecklistViewer[]) => {
      this._viewers.set(viewers);
    });
  }
}
